import { mcpPage } from "./mcpPage";
import { partnersPage } from "./partnersPage";
import { referralPage } from "./referralPage";
import { startupsPage } from "./startupsPage";

/** Copy for static marketing / legal shell pages (About Founder, Mission, Contact, Shariah methodology, legal titles) */
export const staticPages = {
  mcpPage,
  startupsPage,
  referralPage,
  partnersPage,
  legal: {
    eyebrow: "Legal",
    privacyTitle: "Privacy policy",
    termsTitle: "Terms & conditions",
    disclosuresTitle: "Disclosures",
  },
  aboutFounder: {
    pageTitle: "About the founder",
    eyebrow: "Founder & CEO, Akinda",
    name: "Nurmukhammad Ismoilov",
    heroLead:
      "Entrepreneur and educator focused on transparent, Shariah-aligned investing. He leads Akinda (Akinda Capital Technologies LLC) in its mission to help values-driven investors screen and understand global equities with clarity.",
    meta: "Uzbekistan · Business & finance · Islamic finance education",
    backgroundH2: "Background",
    backgroundP1:
      "Nurmukhammad Ismoilov is widely known in Uzbekistan's online finance community as an educator and content creator. Public profiles describe him as a stock trader, author, and entrepreneur with a long-running focus on ethical and Islamic investing education, including work to make market concepts accessible to retail audiences.",
    backgroundP2:
      "He has been associated with {{brand}}, a trading and education brand centered on Islamic principles, and with media and digital projects aimed at helping aspiring investors build skills responsibly.",
    brandName: "NURINVEST.UZ",
    educationH2: "Education & experience",
    educationP1:
      "According to public biographical sources, he was born in Andijan, Uzbekistan, and studied business administration at Ritsumeikan Asia Pacific University (Japan), graduating in 2020. He has described combining academic training with hands-on learning in markets, including professional certifications in areas such as financial markets, corporate finance, and trading fundamentals.",
    educationP2:
      "His professional path has included collaboration with international digital and media companies and building Uzbekistan-based ventures in advertising and online education.",
    akindaH2: "Akinda",
    akindaP1:
      "As founder and CEO of Akinda, Ismoilov steers product vision toward transparent screening, strong data partnerships, and tools that respect both modern markets and Islamic finance criteria. Akinda is operated by {{company}} (Delaware, United States).",
    companyLegalName: "Akinda Capital Technologies LLC",
    linkedIn: "LinkedIn profile",
    instagram: "Instagram",
    photoCaption: "Nurmukhammad — CEO & Founder",
    photosAriaLabel: "Photos",
    portraitAlt: "Nurmukhammad Ismoilov",
  },
  ourMission: {
    eyebrow: "Mission",
    title: "Our mission",
    lead:
      "Make halal and ethical investing accessible through transparent screening, honest data, and workflows that respect both faith principles and how real markets work.",
    foundationKicker: "Foundation",
    believeHeading: "What we believe",
    believeBody:
      "Investors should not have to choose between rigor and values. We believe Shariah-aligned investing deserves the same level of clarity, tooling, and context that conventional platforms offer—without hiding methodology behind vague labels.",
    principlesKicker: "Principles",
    deliverHeading: "How we deliver",
    serveHeading: "Who we serve",
    serveBody:
      "From first-time halal investors to analysts who need dependable screens and issuer context, Akinda exists to shorten the distance between intention and informed action—always with the reminder that platform content is educational and not personalized investment advice.",
    pillars: {
      transparency: {
        title: "Transparency",
        text: "We explain how securities are screened and what data powers each view.",
      },
      education: {
        title: "Education",
        text: "We invest in labels, explainers, and context so results are easier to interpret.",
      },
      integrity: {
        title: "Integrity",
        text: "We disclose limits, avoid hype, and improve screening as standards and data evolve.",
      },
      community: {
        title: "Community",
        text: "We build for a global audience united by investing with conviction and conscience.",
      },
    },
  },
  contactPage: {
    eyebrow: "Get in touch",
    title: "Contact us",
    subtitle:
      "Send us an email for general questions, partnerships, or media. We reply on business days.",
    emailLabel: "Email",
    hint: "For account-specific issues, signed-in users can use in-app support where available.",
  },
  communityPage: {
    eyebrow: "Community",
    title: "Join our Community",
    subtitle:
      "Connect with other investors, share insights, and stay updated.",
    channelLabel: "Telegram",
    cta: "Join Telegram Community",
    hint: "Open to all users. Join the discussion today.",
  },
  shariahMethodology: {
    eyebrow: "Transparency",
    heroTitle: "Shariah screening methodology",
    heroSubtitle:
      "A transparent guide to automated halal investment analysis — how Akinda evaluates every stock against internationally recognized AAOIFI standards.",
    introPart1:
      "At Akinda, we believe faith-based investing must be accurate, transparent, and grounded in real data. To ensure every stock on our platform aligns with Islamic financial principles, our system is built on the ",
    introAaofi:
      "AAOIFI (Accounting and Auditing Organization for Islamic Financial Institutions)",
    introPart2: " standards.",
    trustCalloutText:
      "Our Shariah oversight and screening methodology are independently reviewed on",
    trustCalloutSuffix:
      ", the independent comparison platform for halal investing in the US.",
    dataHeading: "How we gather our data",
    dataLead:
      "Financial reports can be highly complex. To ensure maximum accuracy, Akinda uses a dual-layer data verification system:",
    data1Title: "1. Structured market data",
    data1Body:
      "We use institutional-grade financial APIs (such as FMP) to pull exact, real-time pricing and standard balance sheet metrics.",
    data2Title: "2. AI-powered SEC extraction",
    data2Body:
      "For hidden details — e.g. breaking down sources of interest income or complex debt structures — our custom AI auditor reads and extracts variables from official SEC company filings.",
    phaseLabel: "Screening phase",
    phase1Title: "Phase 1: Business activity (sector tolerance)",
    phase1P1:
      "Before analyzing financial ratios, we examine the core business. A company cannot be considered halal if its primary operations involve prohibited (haram) activities.",
    phase1P2Bold: "Automatic exclusions (0% tolerance):",
    phase1P2Text:
      "a company is flagged as haram if its primary business involves conventional banking, insurance, or interest-based financial services; alcohol; pork; gambling and casinos; adult entertainment; tobacco; or weapons manufacturing.",
    phase1P3Bold: "The 5% incidental income rule:",
    phase1P3Text:
      "large permissible companies may earn a small fraction of revenue from interest in bank accounts or minor non-compliant side businesses.",
    formula: "Formula",
    formula5pct:
      "(Non-permissible revenue ÷ Total revenue) × 100",
    note5pctBefore:
      "If non-permissible income is ",
    note5pctStrong: "greater than 5%",
    note5pctAfter:
      " of total revenue, the stock is marked haram. If it is under 5%, it passes this screen.",
    phase2Title: "Phase 2: Financial ratio screening",
    phase2P1:
      "Even if a company sells permissible products, it can still fail if it relies on excessive debt or earns money by hoarding cash for interest. We calculate three ratios based on AAOIFI standards.",
    phase2P2Bold: "36-month market capitalization rule:",
    phase2P2Text:
      "for debt and investment ratios, AAOIFI requires comparing financials to the company's 36-month average market value. We use:",
    avgMcapTitle: "Average market cap",
    avgMcapFormula:
      "(Sum of market capitalization at the end of the last 12 quarters) ÷ 12",
    phase2P3:
      "To pass this phase and be labeled halal, the company must meet all three limits below.",
    ratio1Title: "1. Debt limit (< 30%)",
    ratio1Body:
      "A permissible company cannot be built on excessive interest-bearing loans.",
    ratio1Formula:
      "(Total interest-bearing debt ÷ Average 12-quarter market cap) × 100 < 30%",
    ratio1Note:
      "Variables: short-term debt + long-term debt + capital lease obligations. Operating liabilities (e.g. payables) are excluded when they do not bear interest.",
    ratio2Title: "2. Interest-bearing investment limit (< 30%)",
    ratio2Body:
      "Cash should support real operations — not mimic a bank by parking funds in interest-bearing instruments.",
    ratio2Formula:
      "(Total interest-bearing securities and deposits ÷ Average 12-quarter market cap) × 100 < 30%",
    ratio2Note:
      "Variables: interest-bearing cash equivalents + short-term investments + long-term interest-bearing investments.",
    ratio3Title: "3. Liquidity limit (< 30%)",
    ratio3Body:
      "The company must hold real assets — not only liquid cash traded for profit.",
    ratio3Formula:
      "((Total cash + Accounts receivable) ÷ Total assets) × 100 < 30%",
    ratio3Note:
      "Variables: cash & cash equivalents + accounts receivable, divided by total assets.",
    phase3Label: "Outcome",
    phase3Title: "Phase 3: Final verdict & purification",
    phase3P1:
      "After our AI auditor processes SEC filings and API data through these formulas, each stock receives a clear status:",
    verdictHalal: "Halal",
    verdictHalalDesc:
      "Passed both business activity and financial ratio screens (permissible).",
    verdictHaram: "Haram",
    verdictHaramDesc:
      "Failed the 5% revenue rule or one of the 30% financial limits.",
    verdictDoubt: "Doubtful",
    verdictDoubtDesc:
      "Data incomplete, or a major merger/acquisition temporarily obscures true financials.",
    purificationBold: "A note on purification:",
    purificationText:
      "if a halal stock has a small amount of non-permissible income (e.g. 2%, which passes the 5% rule), the investor should purify dividends by donating that portion to charity. Akinda calculates and provides this purification percentage in detailed reports so you know exactly what to do.",
    advisorKicker: "Shariah advisor",
    advisorName: "Dr. Muhammad Nazir Khan",
    advisorBio:
      "Dr. Khan advises Akinda on Shariah screening methodology and alignment with AAOIFI-oriented principles. His guidance helps us keep our rules transparent, consistent, and grounded in recognized Islamic finance practice — including certification perspectives relevant to compliant securities analysis (CSAA).",
    linkedInProfile: "LinkedIn profile",
    advisorPhotoAlt: "Dr. Muhammad Nazir Khan",
    ctaText: "Ready to explore halal-screened equities on the platform?",
    ctaLink: "Open screening",
  },
  forBusiness: {
    metaTitle: "Halal Stock API - Shariah Compliance Data",
    heroBadge: "AAOIFI Standard · 20,000+ stocks coverage",
    heroTitle: "Shariah-compliant stock data API for your platform",
    heroLead:
      "The API built for Islamic finance. Get AAOIFI-compliant screening, AI-powered SEC filing analysis, and proprietary halal stock scores — all via simple REST endpoints.",
    heroCtaPrimary: "Start for free",
    heroCtaSecondary: "View API docs",
    statStocks: "20,000+ covered stocks & ETFs",
    statStandard: "AAOIFI-aligned screening",
    whatYouGetKicker: "What you get",
    whatYouGetTitle: "Comprehensive halal stock data",
    whatYouGetSubtitle:
      "Transparent Islamic financial criteria, ready for search bars, watchlists, and compliance dashboards.",
    features: {
      debt: {
        title: "Debt-to-equity analysis",
        text: "Interest-bearing debt measured against total assets. Every ticker screened against the AAOIFI threshold so you know exactly where each company stands.",
      },
      liquidity: {
        title: "Liquid assets vs market cap",
        text: "Cash and receivables compared against market capitalisation. See whether intrinsic value comes from tangible assets or intangibles.",
      },
      impure: {
        title: "Impure income detection",
        text: "AI-extracted prohibited revenue streams from SEC 10-K and 10-Q footnotes — interest income, operating-lease debt, and haram segment revenue.",
      },
      ratios: {
        title: "Financial ratios (basic report)",
        text: "Debt, liquidity, and impure-income ratios from the latest filings, returned as ready-to-use decimals for a fast halal verdict.",
      },
      ai: {
        title: "AI SEC filing analysis",
        text: "Our model reads 10-K and 10-Q footnotes the way a Shariah analyst would — surfacing details other screeners miss.",
      },
      full: {
        title: "Full compliance reports",
        text: "Halal status, Akinda halal rating, every underlying ratio, and the AI-extracted breakdown — one endpoint, one JSON response.",
      },
    },
    whyKicker: "Why Akinda API",
    whyTitle: "Built for fintech, banks, and robo-advisors",
    whyItems: {
      coverage: {
        title: "Broad global coverage",
        text: "Access 20,000+ stocks and ETFs screened for Shariah compliance — enough breadth to power diversified portfolio tools without building screening in-house.",
        visualAlt: "Top halal stocks screener showing Shariah-compliant companies",
      },
      methodology: {
        title: "Proprietary AAOIFI methodology",
        text: "Our certified screening ranks securities with precision using debt, liquidity, and impure-income thresholds aligned with widely accepted Islamic finance standards.",
        visualAlt: "Halal stock analysis dashboard with compliance breakdown",
      },
      integration: {
        title: "Developer-first REST integration",
        text: "One API call returns a full compliance verdict or detailed report. Ship halal screening into your app with JSON endpoints and clear documentation.",
        visualAlt: "Akinda platform tools for building halal investment products",
      },
    },
    integrationKicker: "Simple integration",
    integrationTitle: "One call. One JSON response.",
    integrationSubtitle:
      "Three endpoints cover quick status checks, ratio breakdowns, and full AI-powered SEC analysis.",
    endpoints: {
      compliance: {
        title: "Shariah compliance status",
        text: "The fastest halal / non-halal verdict for search bars, watchlists, and live feed filters.",
      },
      basic: {
        title: "Basic report — financial ratios",
        text: "The three AAOIFI ratios from the latest 10-K / 10-Q plus the halal rating for transparent screening UI.",
      },
      full: {
        title: "Full report — AI SEC analysis",
        text: "Everything in the basic report plus interest debt, operating leases, prohibited revenue, and last-scanned timestamp.",
      },
    },
    plansDevelopersTitle: "For developers",
    plansDevelopersText:
      "Indie devs and retail trading tools. Pay-as-you-scale personal plans, full endpoint access, zero setup.",
    plansDevelopersCta: "View personal plans",
    plansBusinessTitle: "For business",
    plansBusinessText:
      "Banks, hedge funds, robo-advisors, and fintech platforms. High-throughput plans, dedicated SLAs, and commercial-use terms.",
    plansBusinessCta: "View business plans",
    faqTitle: "Frequently asked questions",
    faqSubtitle:
      "Everything you need to know about Akinda API and our screening methodology.",
    faq: {
      q1: {
        q: "What screening methodology do you use?",
        a: "We follow AAOIFI (Accounting and Auditing Organization for Islamic Financial Institutions) standards, the most widely accepted framework for Shariah compliance screening. Our thresholds are: debt ratio under 30%, liquidity ratio under 30%, and impure income under 5%.",
      },
      q2: {
        q: "How often is the data updated?",
        a: "Screening data is refreshed as new SEC filings are published and on a regular schedule for market inputs. Full reports include a last-scanned timestamp so you know how current each verdict is.",
      },
      q3: {
        q: "Do you provide purification calculations?",
        a: "Yes. When a stock passes screening but earns a small amount of non-permissible income, our detailed reports include purification guidance so investors know what portion of dividends may need to be donated to charity.",
      },
      q4: {
        q: "Can I use this for commercial applications?",
        a: "Yes. Business plans include commercial-use terms for banks, fintech platforms, robo-advisors, and other products that embed Akinda data. Personal plans are designed for individual developers and smaller tools.",
      },
      q5: {
        q: "What's the difference between Basic and Full reports?",
        a: "The Basic report returns halal status, Akinda halal rating, and the three core AAOIFI financial ratios. The Full report adds AI-extracted figures from SEC filings — interest-bearing debt, operating-lease obligations, prohibited revenue, and a last-scanned date.",
      },
    },
    docsCta: "View full API documentation",
    ctaTitle: "Start building",
    ctaSubtitle:
      "Launch halal screening in your product with Akinda API — AAOIFI-aligned data, developer docs, and plans that scale from indie tools to enterprise platforms.",
    ctaButton: "Start building",
    heroImageAlt: "Business professional representing Akinda API for enterprise clients",
  },
} as const;
